import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useEditConfirm } from "../../context/EditConfirmContext";
import { formatET } from "../../lib/dateUtils";

export function AdminReferrals({ referrals, onRefresh }) {
  const confirmEdit = useEditConfirm();
  const [amounts, setAmounts] = useState({});

  const approve = async (id) => {
    const r = referrals.find((x) => x.id === id);
    if (!(await confirmEdit(`${r?.store_display_name || "this"} referral (approve)`))) return;
    try {
      await api.patch(`/admin/referrals/${id}/approve`, { commission_amount: parseFloat(amounts[id] || 0) });
      toast.success("Referral approved — commission recorded");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reject = async (id) => {
    const r = referrals.find((x) => x.id === id);
    if (!(await confirmEdit(`${r?.store_display_name || "this"} referral (reject)`))) return;
    try {
      await api.patch(`/admin/referrals/${id}/reject`);
      toast.success("Referral rejected");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="bg-igc-surface border border-igc-gold/15 p-6 mt-8" data-testid="admin-referrals-list">
      <p className="text-igc-muted text-xs uppercase tracking-widest mb-4" title="Referral payouts owed to a client for bringing in another paying client. IGC pays them 20% of the commission earned on that new referral once it closes.">Referral Payouts — 20% Client Commission</p>
      {referrals.length === 0 && <p className="text-igc-muted text-sm">No referrals submitted yet.</p>}
      <div className="space-y-2">
        {referrals.map((r) => (
          <div key={r.id} className="border border-igc-gold/10 p-4 flex items-center justify-between gap-4" data-testid={`referral-row-${r.id}`}>
            <div className="flex-1">
              <p className="text-igc-cream text-sm">{r.store_display_name} referred <span className="text-igc-goldText">{r.referred_name}</span> ({r.deal_type})</p>
              <p className="text-igc-muted text-xs">{r.referred_contact} — {formatET(r.created_at, "MMM d, yyyy")}</p>
            </div>
            <span className={`text-[10px] px-2 py-1 tracking-wide ${r.status === "earned" ? "bg-igc-success/10 text-igc-success" : r.status === "rejected" ? "bg-igc-danger/10 text-igc-danger" : "bg-igc-gold/10 text-igc-goldText"}`} title="Pending = waiting on your review. Earned = you approved it and a commission was recorded. Rejected = you declined this referral, no commission owed.">{r.status}</span>
            {r.status === "pending" && (
              <div className="flex items-center gap-2">
                <Input
                  data-testid={`referral-commission-input-${r.id}`}
                  type="number"
                  placeholder="Commission $"
                  title="Type the dollar amount you want to pay this client for referring this new client, then click Approve."
                  value={amounts[r.id] || ""}
                  onChange={(e) => setAmounts({ ...amounts, [r.id]: e.target.value })}
                  className="w-32 bg-igc-surfaceLight border-igc-gold/20 h-9"
                />
                <Button data-testid={`referral-approve-button-${r.id}`} onClick={() => approve(r.id)} title="Approves this referral and permanently records the commission amount above as owed to the referring client." className="bg-igc-success/10 border border-igc-success text-igc-success hover:bg-igc-success hover:text-black h-9">Approve</Button>
                <Button data-testid={`referral-reject-button-${r.id}`} onClick={() => reject(r.id)} title="Declines this referral. No commission will be recorded or paid out." className="bg-igc-danger/10 border border-igc-danger text-igc-danger hover:bg-igc-danger hover:text-black h-9">Reject</Button>
              </div>
            )}
            {r.status === "earned" && <span className="text-igc-success text-sm">${r.commission_amount}</span>}
          </div>
        ))}
      </div>
    </motion.div>
  );
}
